import * as React from 'react';
import { useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import _ from 'lodash';
import styled from 'styled-components';

import { useInit } from '@/lib/react-hooks';

import { LiveUpdates, LiveUpdatesChannel } from '@/api/app/live-updates';
import { ReactNative } from '@/api/app/react-native';
import { appendEvents } from '@/redux/app/actions/event';
import { setSettings } from '@/redux/app/actions/settings';
import type { UserSettings, AccountSettings } from '@/redux/app/reducers/settings';
import { AppSecretsContext } from './secrets/app-secrets';
import { AccountInfoContext } from './account/account-info';
import { DropzoneRootWrapper } from './shared/dropzone';

export interface RootProps {
  accountId: string;
  userSettings: UserSettings;
  accountSettings: AccountSettings;
  children: React.ReactChild|React.ReactChild[];
}

const DropzoneRoot = styled.div`
 width: 100%;
 height: 100%;
`;

export const Root = ({ accountId, userSettings, accountSettings, children }: RootProps) => {
  const dispatch = useDispatch();
  const { rememberToken } = useContext(AppSecretsContext);
  const [count, setCount] = React.useState(0);
  const [title, setTitle] = React.useState(document.title);
  const { subdomain } = useContext(AccountInfoContext);

  useInit(() => dispatch(setSettings({ user: userSettings, account: accountSettings })));

  useEffect(() => {
    const cb = () => setCount(0);
    window.addEventListener('focus', cb);
    window.addEventListener('click', cb);

    return () => {
      window.removeEventListener('focus', cb);
      window.removeEventListener('click', cb);
    };
  }, []);

  useEffect(() => {
    if (window.users_channel) window.users_channel.cleanupSubscriptions();

    const handleBrowserNotifications = (e, currCount) => {
      const notify = () => {
        if (!e.notify || e.direction !== 'in') return;
        document.head.getElementsByTagName('title')[0].innerHTML = `(${currCount}) ${title}`;

        const search = window.location.search || '';
        const browserLink = `${e.deep_link}${search}`;

        // references czNotifier from _browser_notifications.html
        // TODO: migrate the notification JS into a react library
        window.czNotifier?.spawn(e.contact, e.preview, e.ticket.uuid, browserLink);
      };

      switch (e.element?.element_type) {
        case 'SmsMessage':
        case 'Call':
        case 'MmsMessage':
        case 'CustomEvent':
          notify();
          break;
        default:
      }
    };

    window.V3UsersChannel = new LiveUpdates({
      channel: LiveUpdatesChannel.V3_USERS,
      token: rememberToken || Cookies.get('remember_token'),
      accountId,
      onConnect: () => console.log('users channel connected'),
      onBatchReceived: (events) => {
        dispatch(appendEvents(events));
        setCount((currCount) => {
          // Increment notification count by the number of inbound events
          const newCount = currCount + _.sumBy(events, ({ element }) => Number(element.direction === 'in'));
          events.forEach((e) => handleBrowserNotifications(e, newCount));
          return newCount;
        });
      },
    });
    return window.V3UsersChannel.unsubscribe;
  }, [accountId]);

  const [lastUpdated, setLastUpdated] = React.useState(0);

  useEffect(() => {
    if (window.unaddressed_channel) window.unaddressed_channel.cleanupSubscriptions();

    const appendEmptyBadge = (a) => {
      const emptyBadge = document.createElement('div');
      emptyBadge.classList.add('badge');
      const emptyCount = document.createElement('div');
      emptyCount.classList.add('count');
      emptyBadge.append(emptyCount);
      a.append(emptyBadge);
      return emptyBadge;
    };

    const handleBadgeNotifications = (e) => {
      // accounts represents mobile and non-mobile view
      const accounts = document.querySelectorAll(`a[data-account-id="${e.account_id}"]`);
      const unaddressedCount = parseInt(e.unaddressed_count, 10);
      if (unaddressedCount === 0) {
        accounts.forEach((a) => {
          const badge = a.getElementsByClassName('badge')[0];
          if (badge) badge.remove();
        });
        return;
      }

      accounts.forEach((a) => {
        let badge = a.getElementsByClassName('badge')[0];
        if (!badge) badge = appendEmptyBadge(a);
        const newHTML = unaddressedCount < 999 ? unaddressedCount.toString() : '999+';
        badge.innerHTML = newHTML;
      });
    };

    window.V3UnaddressedChannel = new LiveUpdates({
      channel: LiveUpdatesChannel.V3_UNADDRESSED,
      token: rememberToken || Cookies.get('remember_token'),
      subdomain,
      accountId,
      onConnect: () => console.log('unanddressed channel connected'),
      onBatchReceived: (events) => {
        const updateEvent = _.findLast(events, ({ event_type }) => event_type === 'unaddressed_updated');
        if (updateEvent) handleBadgeNotifications(updateEvent);

        setLastUpdated((currlastUpdated) => {
          if (currlastUpdated > _.last(events).updated_at) console.warn('dropping old data', events);
          return lastUpdated;
        });
      },
    });
    return window.V3UnaddressedChannel.unsubscribe;
  }, []);

  useEffect(() => {
    ReactNative.attach(dispatch, {
      'react-native:unfreeze': () => {
        window.V3UsersChannel.fetchMissedEvents().catch(() => {
          window.location.reload();
        });
      },
    });
    return () => ReactNative.detach();
  }, []);

  return (
    <DropzoneRootWrapper>
      <DropzoneRoot id="dropzone-root">
        {children}
      </DropzoneRoot>
    </DropzoneRootWrapper>
  );
};
